/**
 * Path Resolver
 *
 * Handles template path expansion and matching with caching for performance.
 * Template paths use [] as placeholder for array indices (e.g., "dataAssets>[]>item")
 */

import { FieldPath } from './types';

interface PathExpansionCache {
  /** Array length at time of expansion */
  arrayLength: number;
  /** Expanded paths */
  paths: FieldPath[];
}

/**
 * Resolves and expands template paths for array fields
 */
export class PathResolver {
  private separator: string;
  private expansionCache = new Map<string, PathExpansionCache>();
  private patternCache = new Map<string, RegExp>();

  constructor(separator: string) {
    this.separator = separator;
  }

  /**
   * Expands a template path to actual indexed paths
   * Template: "dataAssets>[]>item"
   * Result: ["dataAssets>0>item", "dataAssets>1>item", ...]
   *
   * @param templatePath - Path with [] markers
   * @param formData - Current form data to get array length
   * @returns Array of expanded paths
   */
  expandTemplatePath(templatePath: FieldPath, formData: any): FieldPath[] {
    const segments = templatePath.split(this.separator);

    // Find the first array segment
    const arraySegmentIndex = segments.findIndex(s => s === '[]');
    if (arraySegmentIndex === -1) {
      // No arrays to expand
      return [templatePath];
    }

    // Navigate to the array in form data
    const beforeArray = segments.slice(0, arraySegmentIndex);
    let currentData = formData;
    for (const segment of beforeArray) {
      currentData = currentData?.[segment];
    }

    if (!Array.isArray(currentData)) {
      // Can't expand if not an array
      return [templatePath];
    }

    const arrayLength = currentData.length;

    // Check cache
    const cacheKey = this.getCacheKey(templatePath, beforeArray);
    const cached = this.expansionCache.get(cacheKey);

    if (cached && cached.arrayLength === arrayLength) {
      // Cache hit with same array length
      return cached.paths;
    }

    // Generate all indexed paths
    const result: FieldPath[] = [];
    for (let i = 0; i < arrayLength; i++) {
      const indexedPath = [
        ...segments.slice(0, arraySegmentIndex),
        i.toString(),
        ...segments.slice(arraySegmentIndex + 1),
      ].join(this.separator);
      result.push(indexedPath);
    }

    // Update cache
    this.expansionCache.set(cacheKey, {
      arrayLength,
      paths: result,
    });

    return result;
  }

  /**
   * Checks if an actual path matches a template path
   * Template: "dataAssets>[]>item"
   * Actual: "dataAssets>0>item" -> true
   * Actual: "dataAssets>999>item" -> true
   * Actual: "otherField>0>item" -> false
   *
   * @param actualPath - The actual path to check
   * @param templatePath - The template path to match against
   * @returns True if matches, false otherwise
   */
  matches(actualPath: FieldPath, templatePath: FieldPath): boolean {
    // Exact match
    if (actualPath === templatePath) {
      return true;
    }

    // Check if template has array markers
    if (!templatePath.includes(`${this.separator}[]`)) {
      return false;
    }

    // Get or create regex pattern
    const pattern = this.getPattern(templatePath);
    return pattern.test(actualPath);
  }

  /**
   * Gets a compiled regex pattern for a template path (with caching)
   *
   * @param templatePath - Template path with [] markers
   * @returns Compiled regex pattern
   */
  private getPattern(templatePath: FieldPath): RegExp {
    if (this.patternCache.has(templatePath)) {
      return this.patternCache.get(templatePath)!;
    }

    // Escape separator for regex
    const escapedSeparator = this.separator.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&',
    );

    // Convert template to regex pattern
    // "dataAssets>[]>item" -> "^dataAssets>\d+>item$"
    const pattern = templatePath.replace(
      new RegExp(`${escapedSeparator}\\[\\]`, 'g'),
      `${escapedSeparator}\\d+`,
    );

    const regex = new RegExp(`^${pattern}$`);
    this.patternCache.set(templatePath, regex);

    return regex;
  }

  /**
   * Generates a cache key for template expansion
   *
   * @param templatePath - Template path
   * @param beforeArray - Segments before the array marker
   * @returns Cache key
   */
  private getCacheKey(templatePath: FieldPath, beforeArray: string[]): string {
    return `${templatePath}::${beforeArray.join(this.separator)}`;
  }

  /**
   * Clears all caches
   */
  clearCache(): void {
    this.expansionCache.clear();
    this.patternCache.clear();
  }

  /**
   * Invalidates cache for a specific array path
   * Call this when an array's length changes
   *
   * @param arrayPath - Path to the array (e.g., "dataAssets")
   */
  invalidateCacheForArray(arrayPath: string): void {
    // Remove all cache entries that start with this array path
    for (const key of this.expansionCache.keys()) {
      if (key.startsWith(`${arrayPath}${this.separator}`)) {
        this.expansionCache.delete(key);
      }
    }
  }

  /**
   * Converts an indexed path to a template path
   * "dataAssets>0>item" -> "dataAssets>[]>item"
   *
   * @param indexedPath - Path with numeric indices
   * @returns Template path with [] markers
   */
  toTemplatePath(indexedPath: FieldPath): FieldPath {
    const segments = indexedPath.split(this.separator);
    const templateSegments = segments.map(seg =>
      /^\d+$/.test(seg) ? '[]' : seg,
    );
    return templateSegments.join(this.separator);
  }

  /**
   * Gets cache statistics (for debugging)
   */
  getCacheStats(): {
    expansionCacheSize: number;
    patternCacheSize: number;
  } {
    return {
      expansionCacheSize: this.expansionCache.size,
      patternCacheSize: this.patternCache.size,
    };
  }
}
